import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, ShieldCheck, FileCheck2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-white">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#064E3B] text-white overflow-hidden grain">
        <img src="/img/riyadh.jpg" alt="Riyadh skyline" className="absolute inset-0 w-full h-full object-cover opacity-25 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-[#064E3B]/60" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 rounded bg-amber-500 flex items-center justify-center font-bold text-[#064E3B] text-lg">﷼</div>
          <div className="font-display text-lg font-semibold">KSA VAT Bill Entry</div>
        </div>
        <div className="relative z-10 max-w-lg">
          <div className="text-[11px] uppercase tracking-[0.2em] text-amber-300 mb-4">Bill Entry & Management System</div>
          <h1 className="text-4xl xl:text-5xl font-semibold leading-[1.1]">Every invoice recorded. Every change accountable.</h1>
          <p className="text-emerald-100/80 mt-5 leading-relaxed">Centralised client bill registers with linked source documents, auditor review and an append-only audit trail — on Riyadh time, in Saudi Riyal.</p>
          <div className="grid grid-cols-3 gap-4 mt-10">
            {[[ShieldCheck, "Role-based access"], [FileCheck2, "Linked attachments"], [Clock, "Asia/Riyadh timestamps"]].map(([Icon, t]) => (
              <div key={t} className="border border-white/15 rounded p-3 bg-white/5 backdrop-blur-sm">
                <Icon className="w-4 h-4 text-amber-300 mb-2" />
                <div className="text-xs text-emerald-50">{t}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-emerald-200/60">For record management and auditor review. Not a replacement for statutory VAT return filing.</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <div className="eyebrow mb-2">Secure sign in</div>
          <h2 className="text-3xl font-semibold text-slate-900">Welcome back</h2>
          <p className="text-sm text-slate-500 mt-1.5 mb-8">Sign in with the account issued by your administrator.</p>
          <label className="block mb-4">
            <span className="text-xs font-medium text-slate-600">Email or username</span>
            <div className="relative mt-1.5">
              <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9 h-11" placeholder="name@company.com" autoFocus required data-testid="login-email-input" />
            </div>
          </label>
          <label className="block mb-6">
            <span className="text-xs font-medium text-slate-600">Password</span>
            <div className="relative mt-1.5">
              <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9 h-11" placeholder="••••••••" required data-testid="login-password-input" />
            </div>
          </label>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-4" data-testid="login-error">{error}</div>}
          <Button type="submit" disabled={busy} className="w-full h-11 bg-emerald-800 hover:bg-emerald-900" data-testid="login-submit-button">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-[11px] text-slate-400 mt-6 text-center">Accounts lock for 15 minutes after 5 failed attempts.</p>
        </form>
      </div>
    </div>
  );
}
